<script setup>
    import { computed, toRefs } from "vue";
    import moment from "moment";
    import Button from "@/components/Button.vue";
    import { diffMinutes } from "@/tabs/Operation/calendar-utils";
    import { getConfig, getErrorMessage, klinikenApi } from "@/api";
    import { openDialog } from "@/utils";
    import Operationsinformation from "@/tabs/Operation/Operationsinformation.vue";
    import Operationsjournal from "@/tabs/Operation/Operationsjournal.vue";
    import { useStore } from "@/store";
    import NivaFranTill from "@/tabs/Operation/NivaFranTill.vue";
    import { ConfigModuleName, ConfigType, OperationConfigModuleName } from "@/store/modules/types/configModuleTypes";

    const store = useStore();

    function isVisible(name, type = ConfigType.Field) {
        return store.getters["config/shouldBeVisibleByName"]({
            moduleName: ConfigModuleName.Operation,
            subModuleName: OperationConfigModuleName.PagaendeOperationDetaljinfo,
            type,
            name,
        });
    }

    const props = defineProps({
        event: {
            type: Object,
            required: true,
        },
        showButtons: {
            type: Boolean,
            default: false,
        },
        canReadOperationsjournal: {
            type: Boolean,
            default: false,
        },
        showPersonID: {
            type: Boolean,
            default: false,
        },
    });

    const { event, showButtons, canReadOperationsjournal, showPersonID } = toRefs(props);

    // Döljer/visar sektionsblocken utifrån innehållsinställningar. Avgränsarna (<hr>) mellan blocken villkoras mot
    // dessa så att en avgränsare aldrig ritas ut om det inte finns ett synligt block ovanför den.
    const showTidsblock = computed(
        () => isVisible("tidsforlopp", ConfigType.Section) || isVisible("klockslag", ConfigType.Section)
    );
    const showOperationsdetaljerBlock = computed(() => isVisible("operationsdetaljer", ConfigType.Section));
    const showPreanestesibedomningBlock = computed(
        () =>
            !!event.value.extendedProps.preanestesibedomningInformation &&
            isVisible("preanestesibedomning_info") &&
            isVisible("preanestesibedomning", ConfigType.Section)
    );

    const operationXMinuterKvar = computed(() => {
        return store.state.systemData.operationXMinuterKvar;
    });

    const enableActivePatient = computed(() => {
        return store.state.loginMode.enableActivePatient;
    });

    const now = computed(() => {
        return moment().format("HH:mm");
    });

    const statusPagaendeOrGenomford = computed(() => {
        return ["Pågående", "Genomförd"].includes(event.value.extendedProps.status);
    });

    const showOperationsjournalLink = computed(() => {
        if (canReadOperationsjournal.value) {
            return moment(event.value.start).isSameOrBefore(moment(), "day") || statusPagaendeOrGenomford.value;
        }
        return false;
    });

    function padMinutes(minutes) {
        return `${minutes}`.padStart(2, "0");
    }

    function formatTime(datetime) {
        return moment(datetime).format("HH:mm");
    }

    const opjournalTider = computed(() => event.value.extendedProps.opjournalTider);

    const forberedelseMinuter = computed(() =>
        diffMinutes(opjournalTider.value.patienttid_start, opjournalTider.value.opstart)
    );

    const operationMinuter = computed(() => diffMinutes(opjournalTider.value.opstart, opjournalTider.value.opslut));

    const avvecklingMinuter = computed(() =>
        diffMinutes(opjournalTider.value.opslut, opjournalTider.value.efterarbete_sal_slut)
    );

    const salstidMinuter = computed(() =>
        diffMinutes(opjournalTider.value.patienttid_start, opjournalTider.value.efterarbete_sal_slut)
    );

    async function openOperationsinformation() {
        const patient = event.value.extendedProps.patient;
        const anmalanId = event.value.extendedProps.anmalanId;

        if (enableActivePatient.value && patient) {
            await store.dispatch("patientData/loadFromBackend", {
                id: patient.id,
                failSilently: false,
            });
        }

        try {
            await store.dispatch("tabs/closeTab", Operationsinformation, { root: true });
            await store.dispatch("tabs/openTab", {
                component: Operationsinformation,
                parameters: { operationsanmalan_id: anmalanId },
            });
        } catch (error) {
            openDialog(getErrorMessage(error), "error");
        }
    }

    async function openOperationsjournal() {
        const patient = event.value.extendedProps.patient;
        const anmalanId = event.value.extendedProps.anmalanId;

        if (enableActivePatient.value && patient) {
            await store.dispatch("patientData/loadFromBackend", {
                id: patient.id,
                failSilently: false,
            });
        }

        try {
            await store.dispatch("tabs/closeTab", Operationsjournal, { root: true });
            await store.dispatch("tabs/openTab", {
                component: Operationsjournal,
                parameters: { operationsanmalan_id: anmalanId },
            });
        } catch (error) {
            openDialog(getErrorMessage(error), "error");
        }
    }
</script>

<template>
    <div class="pagaende-operation-detaljinfo">
        <div class="detaljinfo-header">
            <span class="header-field">
                <img src="@/assets/actionIcons/opstart_i_opjournal.svg" />
                <span>{{ event.extendedProps.opkod.code }}</span>
            </span>
            <span class="header-field">
                <img src="@/assets/actionIcons/add_operator.svg" />
                <span>{{ event.extendedProps.huvudoperator?.user_display?.username }}</span>
            </span>
            <span v-if="event.extendedProps.patient && showPersonID" class="header-field patient">
                <img src="@/assets/actionIcons/add_patient.svg" />
                <span>
                    {{ event.extendedProps.patient.personId_formatted }}
                </span>
                <span class="patient-name">
                    {{ event.extendedProps.patient.namn }}
                </span>
            </span>
        </div>
        <hr v-if="showTidsblock" />
        <div v-if="showTidsblock" class="detaljinfo-body">
            <table v-if="isVisible('tidsforlopp', ConfigType.Section)">
                <tr v-if="isVisible('planerad_optid')">
                    <td class="label">Planerad optid</td>
                    <td :class="{ time: true }">{{ event.extendedProps.planerad_optid }} min</td>
                </tr>
                <tr v-if="isVisible('forberedelse_kvar')">
                    <td class="label">Förberedelse kvar</td>
                    <td :class="{ time: true, bold: event.extendedProps.aktivOpdel > 1 }">
                        {{ padMinutes(event.extendedProps.forberedelseKvar) }} min
                    </td>
                    <td v-if="event.extendedProps.aktivOpdel < 1" class="status light">
                        <img src="@/assets/pagaop_prognos.svg" />
                        <span>Prognos</span>
                    </td>
                    <td v-else-if="event.extendedProps.aktivOpdel === 1" class="status">
                        <img v-if="event.extendedProps.forberedelserITid" src="@/assets/pagaop_on_time_green.svg" />
                        <img v-else src="@/assets/pagaop_late_red.svg" />
                        <span>Pågående</span>
                    </td>
                    <td v-else class="status bold">
                        <img v-if="event.extendedProps.forberedelserITid" src="@/assets/pagaop_on_time_green.svg" />
                        <img v-else src="@/assets/pagaop_late_red.svg" />
                        <span>Genomförd</span>
                    </td>
                    <td class="tid-td">
                        <template v-if="forberedelseMinuter !== null">
                            <span class="tid-label">Tid</span> <span>{{ forberedelseMinuter }} min</span>
                        </template>
                    </td>
                </tr>
                <tr v-if="isVisible('optid_kvar')">
                    <td class="label">Operationstid kvar</td>
                    <td :class="{ time: true, bold: event.extendedProps.aktivOpdel > 2 }">
                        {{ padMinutes(event.extendedProps.optidKvar) }} min
                    </td>
                    <td v-if="event.extendedProps.aktivOpdel < 2" class="status light">
                        <img src="@/assets/pagaop_prognos.svg" />
                        <span>Prognos</span>
                    </td>
                    <td v-else-if="event.extendedProps.aktivOpdel === 2" class="status">
                        <img v-if="event.extendedProps.optidITid" src="@/assets/pagaop_on_time_green.svg" />
                        <img v-else src="@/assets/pagaop_late_red.svg" />
                        <span>Pågående</span>
                    </td>
                    <td v-else class="status bold">
                        <img v-if="event.extendedProps.optidITid" src="@/assets/pagaop_on_time_green.svg" />
                        <img v-else src="@/assets/pagaop_late_red.svg" />
                        <span>Genomförd</span>
                    </td>
                    <td class="tid-td">
                        <template v-if="operationMinuter !== null">
                            <span class="tid-label">Tid</span> <span>{{ operationMinuter }} min</span>
                        </template>
                    </td>
                </tr>
                <tr v-if="isVisible('avveckling_kvar')">
                    <td class="label">Avveckling kvar</td>
                    <td :class="{ time: true, bold: event.extendedProps.aktivOpdel > 3 }">
                        {{ padMinutes(event.extendedProps.avvecklingKvar) }} min
                    </td>
                    <td v-if="event.extendedProps.aktivOpdel < 3" class="status light">
                        <img src="@/assets/pagaop_prognos.svg" />
                        <span>Prognos</span>
                    </td>
                    <td v-else-if="event.extendedProps.aktivOpdel === 3" class="status">
                        <img v-if="event.extendedProps.avvecklingITid" src="@/assets/pagaop_on_time_green.svg" />
                        <img v-else src="@/assets/pagaop_late_red.svg" />
                        <span>Pågående</span>
                    </td>
                    <td v-else class="status bold">
                        <img v-if="event.extendedProps.avvecklingITid" src="@/assets/pagaop_on_time_green.svg" />
                        <img v-else src="@/assets/pagaop_late_red.svg" />
                        <span>Genomförd</span>
                    </td>
                    <td class="tid-td">
                        <template v-if="avvecklingMinuter !== null">
                            <span class="tid-label">Tid</span> <span>{{ avvecklingMinuter }} min</span>
                        </template>
                    </td>
                </tr>
                <tr v-if="isVisible('salstid_kvar')">
                    <td class="label">Salstid kvar</td>
                    <td :class="{ time: true, bold: event.extendedProps.aktivOpdel > 3 }">
                        {{ padMinutes(event.extendedProps.salstidKvar) }} min
                    </td>
                    <td></td>
                    <td class="tid-td">
                        <template v-if="salstidMinuter !== null">
                            <span class="tid-label">Tid</span> <span>{{ salstidMinuter }} min</span>
                        </template>
                    </td>
                </tr>
            </table>
            <table v-if="isVisible('klockslag', ConfigType.Section)">
                <tr v-if="isVisible('nu')">
                    <td class="label">Tid just nu</td>
                    <td class="time">{{ now }}</td>
                </tr>
                <tr v-if="isVisible('ankomst_preop')">
                    <td class="label">Ankomst preop</td>
                    <template v-if="event.extendedProps.opjournalTider.ankomsttid_preopenhet">
                        <td class="time bold">
                            {{ formatTime(event.extendedProps.opjournalTider.ankomsttid_preopenhet) }}
                        </td>
                        <td class="status bold">
                            <img src="@/assets/pagaop_done_grey.svg" />
                            <span>Genomförd</span>
                        </td>
                    </template>
                </tr>
                <tr v-if="isVisible('patienttid_start')">
                    <td class="label">Patienttid start</td>
                    <template v-if="event.extendedProps.opjournalTider.patienttid_start">
                        <td class="time bold">
                            {{ formatTime(event.extendedProps.opjournalTider.patienttid_start) }}
                        </td>
                        <td class="status bold">
                            <img src="@/assets/pagaop_done_grey.svg" />
                            <span>Genomförd</span>
                        </td>
                    </template>
                    <template v-else>
                        <td class="time">{{ formatTime(event.start) }}</td>
                        <td class="status light">
                            <img src="@/assets/pagaop_prognos.svg" />
                            <span>Prognos</span>
                        </td>
                    </template>
                </tr>
                <tr v-if="isVisible('operation_start')">
                    <td class="label">Operation start</td>
                    <template v-if="event.extendedProps.opjournalTider.opstart">
                        <td class="time bold">{{ formatTime(event.extendedProps.opjournalTider.opstart) }}</td>
                        <td class="status bold">
                            <img src="@/assets/pagaop_done_grey.svg" />
                            <span>Genomförd</span>
                        </td>
                    </template>
                    <template v-else>
                        <td class="time">{{ formatTime(event.extendedProps.optidStart) }}</td>
                        <td class="status light">
                            <img src="@/assets/pagaop_prognos.svg" />
                            <span>Prognos</span>
                        </td>
                    </template>
                </tr>
                <tr v-if="isVisible('x_minuter_kvar')">
                    <td class="label">{{ operationXMinuterKvar }} minuter kvar</td>
                    <template v-if="event.extendedProps.opjournalTider.x_minuter_kvar">
                        <td class="time bold">{{ formatTime(event.extendedProps.opjournalTider.x_minuter_kvar) }}</td>
                        <td class="status bold">
                            <img src="@/assets/pagaop_done_grey.svg" />
                            <span>Genomförd</span>
                        </td>
                    </template>
                </tr>
                <tr v-if="isVisible('operation_slut')">
                    <td class="label">Operation slut</td>
                    <template v-if="event.extendedProps.opjournalTider.opslut">
                        <td class="time bold">{{ formatTime(event.extendedProps.opjournalTider.opslut) }}</td>
                        <td class="status bold">
                            <img src="@/assets/pagaop_done_grey.svg" />
                            <span>Genomförd</span>
                        </td>
                    </template>
                    <template v-else>
                        <td class="time">{{ formatTime(event.extendedProps.avvecklingStart) }}</td>
                        <td class="status light">
                            <img src="@/assets/pagaop_prognos.svg" />
                            <span>Prognos</span>
                        </td>
                    </template>
                </tr>
                <tr v-if="isVisible('efterarbete_sal_slut')">
                    <td class="label">Efterarbete sal slut</td>
                    <template v-if="event.extendedProps.opjournalTider.efterarbete_sal_slut">
                        <td class="time bold">
                            {{ formatTime(event.extendedProps.opjournalTider.efterarbete_sal_slut) }}
                        </td>
                        <td class="status bold">
                            <img src="@/assets/pagaop_done_grey.svg" />
                            <span>Genomförd</span>
                        </td>
                    </template>
                    <template v-else>
                        <td class="time">{{ formatTime(event.end) }}</td>
                        <td class="status light">
                            <img src="@/assets/pagaop_prognos.svg" />
                            <span>Prognos</span>
                        </td>
                    </template>
                </tr>
            </table>
        </div>
        <hr v-if="showTidsblock && showOperationsdetaljerBlock" />
        <div v-if="showOperationsdetaljerBlock" class="detaljinfo-body">
            <table>
                <tr v-if="isVisible('huvudoperator_namn')">
                    <td class="label">Huvudoperatör</td>
                    <td class="value">{{ event.extendedProps.huvudoperator.user_display.name }}</td>
                </tr>
                <tr v-if="isVisible('opsal')">
                    <td class="label">Opsal</td>
                    <td class="value">{{ event.extendedProps.opsal?.namn }}</td>
                </tr>
                <tr v-if="isVisible('typoperation')">
                    <td class="label">Typoperation</td>
                    <td class="value">{{ event.extendedProps.typoperation?.namn }}</td>
                </tr>
                <tr v-if="isVisible('huvudopkod')">
                    <td class="label">Huvudopkod</td>
                    <td class="value">
                        {{ event.extendedProps.opkod.code }} {{ event.extendedProps.opkod.displayName }}
                    </td>
                </tr>
                <tr v-if="isVisible('biopkoder')">
                    <td class="label">Bioperationskod</td>
                    <td>
                        <ul>
                            <li v-for="(biopkod, index) in event.extendedProps.biopkoder" :key="index" class="value">
                                {{ biopkod.code }} {{ biopkod.displayName }}
                            </li>
                        </ul>
                    </td>
                </tr>
                <tr v-if="isVisible('huvuddiagnos')">
                    <td class="label">Huvuddiagnos</td>
                    <td class="value">
                        {{ event.extendedProps.huvuddiagnos.code }} {{ event.extendedProps.huvuddiagnos.displayName }}
                    </td>
                </tr>
                <tr v-if="isVisible('bidiagnoser')">
                    <td class="label">Bidiagnos</td>
                    <td>
                        <ul>
                            <li
                                v-for="(bidiagnos, index) in event.extendedProps.bidiagnoser"
                                :key="index"
                                class="value"
                            >
                                {{ bidiagnos.code }} {{ bidiagnos.displayName }}
                            </li>
                        </ul>
                    </td>
                </tr>
                <tr v-if="isVisible('sida')">
                    <td class="label">Sida</td>
                    <td class="value">{{ event.extendedProps.sida }}</td>
                </tr>
                <tr v-if="event.extendedProps.nivaFranTill && isVisible('nivaer')">
                    <td class="label">Nivåer</td>
                    <td class="value">
                        <NivaFranTill :data="event.extendedProps.nivaFranTill" />
                    </td>
                </tr>
            </table>
        </div>
        <template v-if="showPreanestesibedomningBlock">
            <hr v-if="showTidsblock || showOperationsdetaljerBlock" />
            <div class="detaljinfo-body">
                <div class="label label-header">Preanestesibedömning information</div>
                <div>{{ event.extendedProps.preanestesibedomningInformation }}</div>
            </div>
        </template>
        <template v-if="showButtons">
            <hr v-if="showTidsblock || showOperationsdetaljerBlock || showPreanestesibedomningBlock" />
            <div class="detaljinfo-footer">
                <Button class="link" variant="contour" size="small" @click="openOperationsinformation">
                    <span>Operationsinformation</span>
                    <img class="img-right" src="@/assets/arrow_right_blue.svg" />
                </Button>
                <Button
                    v-if="showOperationsjournalLink"
                    class="link"
                    variant="contour"
                    size="small"
                    @click="openOperationsjournal"
                >
                    <img class="img-left" src="@/assets/opstart_i_opjournal_blue.svg" />
                    <span v-if="statusPagaendeOrGenomford">Redigera operationsjournal</span>
                    <span v-else>Opstart i operationsjournal</span>
                </Button>
            </div>
        </template>
    </div>
</template>

<style lang="scss">
    @import "@/style/_variables.scss";

    .pagaende-operation-detaljinfo {
        padding: 24px;
        width: 468px;
        min-height: 407px;
        background-color: #ffffff;
        border: 1px solid $list-border-color;
        border-radius: 4px;

        font-size: 14px;
        line-height: 17px;
        color: $color-text-black;

        .detaljinfo-header {
            display: flex;
            gap: 12px;
            padding: 0 8px;
            max-width: 412px;
            white-space: nowrap;
            overflow: hidden;
            color: $color-text-popup;

            .header-field {
                display: flex;
                gap: 4px;

                img {
                    height: 17px;
                    width: 17px;
                }

                &.patient {
                    white-space: nowrap;
                    overflow: hidden;

                    .patient-name {
                        white-space: nowrap;
                        overflow: hidden;
                        text-overflow: ellipsis;
                    }
                }
            }
        }

        hr {
            margin-top: 22px;
            border: none;
            border-top: 1px solid $list-border-color;
        }

        .detaljinfo-body {
            .label {
                min-width: 8.375rem;
                color: $color-label-grey;
                font-family:
                    Roboto Medium,
                    sans-serif;
            }

            .label-header {
                margin-top: 1rem;
                margin-bottom: 0.3rem;
            }

            table {
                margin-top: 1rem;

                td {
                    vertical-align: top;
                }

                ul {
                    list-style-type: none;
                    padding: 0;
                    margin: 0;
                }

                .label {
                    text-align: end;
                    padding-right: 10px;
                }

                .time {
                    white-space: nowrap;
                    width: 58px;
                    padding-right: 10px;
                }

                .status {
                    width: 94px;
                    display: flex;
                    gap: 10px;
                }

                .tid-td {
                    padding-left: 10px;
                }

                .tid-label {
                    color: $color-label-grey;
                    font-family:
                        Roboto Medium,
                        sans-serif;
                }

                .value {
                    max-width: 284px;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }

                .bold {
                    font-family:
                        Roboto Medium,
                        sans-serif;
                    font-style: bold;
                }

                .light {
                    color: $color-label-grey;
                }
            }
        }

        .detaljinfo-footer {
            margin-top: 22px;
            display: flex;
            gap: 16px;

            .link {
                font-size: 14px;
                line-height: 17px;
                padding-left: 10px;
                padding-right: 10px;

                .img-right {
                    margin-left: 16px;
                }

                .img-left {
                    margin-right: 6px;
                }
            }
        }
    }
</style>
